import Callout from "components/callout/callout"
import useTargetStorage from "@/hooks/useTargetStorage"
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, memo } from "react"
import { useTranslation } from "react-i18next"
import { Category } from "types"
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as AddIcon } from 'assets/icons/add.svg'
import { useFormContext } from '../forms/form'
import useLayout from "hooks/useLayout"

interface CategorySelectorProps {
    categories: Category[]
    allCategories: Category[]
    selectedCategory?: string
    selectedCategories?: string[]
    setSelectedCategory?: Dispatch<SetStateAction<string>>
    setSelectedCategories?: Dispatch<SetStateAction<string[]>>
    hiddenCategories?: string[]
    allowCreate?: boolean
}

const CategorySelector:React.FC<CategorySelectorProps> = ({
    categories,
    allCategories,
    selectedCategory,
    selectedCategories,
    setSelectedCategory,
    setSelectedCategories,
    hiddenCategories = [],
    allowCreate = true
}) => {

    const  { t } = useTranslation()
    const { disableInputs, isLoading } = useFormContext()
    const { targetStorageDocId: storageId } = useTargetStorage()
    const { openDialog } = useLayout()

    const scrollRef = useRef<HTMLDivElement>(null)

    // Build category map for efficient lookups
    const categoryMap = useMemo(() => {
        const map = new Map<string, Category>()
        allCategories.forEach(category => {
            if (category.id) {
                map.set(category.id, category)
            }
        })
        return map
    }, [allCategories])

    // Compute the current category path for display
    const currentCategoryPath = useMemo(() => {
        if (selectedCategories && selectedCategories.length > 0) {
            return [...selectedCategories]
        } else if (selectedCategory && selectedCategory !== '_') {
            // Build category path from selectedCategory
            let categoryPath = [selectedCategory]
            const visited = new Set<string>()
            let currentCategoryId = selectedCategory

            while (categoryMap.get(currentCategoryId)?.relations?.parentCategory) {
                const parentCategoryId = categoryMap.get(currentCategoryId)?.relations?.parentCategory
                if (parentCategoryId && !visited.has(parentCategoryId)) {
                    visited.add(parentCategoryId)
                    categoryPath.unshift(parentCategoryId)
                    currentCategoryId = parentCategoryId
                } else {
                    break // Prevent infinite loop
                }
            }
            return categoryPath
        }
        return []
    }, [selectedCategory, selectedCategories, categoryMap])

    // Auto-scroll effect
    useEffect(() => {
        if (scrollRef.current && currentCategoryPath.length > 0) {
            setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
                }
            }, 0)
        }
    }, [currentCategoryPath])

    // Get all root categories (categories without parent category)
    const getPossibleRootCategories = useMemo(() => {
        let rootCategories = [] as Category[]
        for (const category of allCategories) {
            let isRoot = true
            let c = category as Category | null
            while (c && c.relations?.parentCategory) {
                const parentCategoryId = c.relations.parentCategory
                c = allCategories.find(cat => cat.id === parentCategoryId) || null
                if (c) {
                    isRoot = false
                    break       
                } else {
                    break
                }
            }
            if (isRoot && category.id && !hiddenCategories.includes(category.id)) {
                rootCategories.push(category)
            }
        }
        return rootCategories
    }, [allCategories, hiddenCategories])

    const getPossibleChildCategories = (parentCategoryId: string) => {
        let childCategories = [] as Category[]
        for (const category of allCategories) {
            if (category.relations?.parentCategory === parentCategoryId) {
                childCategories.push(category)
            } else {
                // Check if the category is a child of the parent category
                let c = category as Category | null
                while (c && c.relations?.parentCategory) {
                    if (c.relations.parentCategory === parentCategoryId) {
                        childCategories.push(category)
                        break
                    } else if (!c.isDeleted) {
                        break
                    }
                    const currentParentId = c ? c.relations?.parentCategory : undefined
                    const parentCategory = currentParentId ? allCategories.find(cat => cat.id === currentParentId) : undefined
                    c = parentCategory || null
                }
            }
        }
        return childCategories.filter(category => category.id && !hiddenCategories.includes(category.id))
    }

    const toggleTag = (tagId: string, level: number) => {
        let newCategoryPath: string[]

        if (level === 0) {
            if (currentCategoryPath[0] === tagId) {
                newCategoryPath = []
            } else {
                newCategoryPath = [tagId]
            }
        } else {
            newCategoryPath = [...currentCategoryPath]
            
            if (newCategoryPath.length > level + 1) {
                newCategoryPath = newCategoryPath.slice(0, level + 1)
            }
            
            if (newCategoryPath[level] === tagId) {
                newCategoryPath = newCategoryPath.slice(0, level)
            } else {
                newCategoryPath[level] = tagId
                // Remove any categories after this level
                newCategoryPath = newCategoryPath.slice(0, level + 1)
            }
        }

        // Update parent state
        const lastCategory = newCategoryPath[newCategoryPath.length - 1] || ''
        
        if (setSelectedCategories) {
            setSelectedCategories(newCategoryPath)
        }
        
        if (setSelectedCategory) {
            setSelectedCategory(lastCategory)
        }
    }

    const colClasses = `flex flex-col gap-2 rounded-md bg-white p-2 border-gray-300 border-[1px]`

    const rowDefaultClasses = `rounded-md p-2 select-none min-w-64 flex justify-between items-center [&>svg]:inline-block [&>svg]:w-5 [&>svg]:h-5 [&>svg]:ml-2 ${disableInputs ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isLoading ? 'animate-pulse' : ''}`
    const rowSelectedClasses = `bg-blue-500 text-white`
    const rowDeletedSelectedClasses = `bg-red-500 text-white [&>svg]:fill-white`
    const rowDeletedUnselectedClasses = `bg-red-200 ${disableInputs && `hover:bg-red-300`} [&>svg]:fill-black`

    const rowUnselectedClasses = `bg-gray-200 text-gray-800 ${disableInputs && `hover:bg-gray-300`}`
    const labelRowClasses = `text-gray-500 text-sm font-semibold select-none text-nowrap`

    const handleCreateCategory = (parentCategoryId: string) => {
        if (!storageId || disableInputs) return
        openDialog('create_category', { storageId, parentCategoryId })
    }

    const renderCreateButton = (parentCategoryId: string) => {
        if (!allowCreate || !storageId) return null
        return (
            <div
                className={`${rowDefaultClasses} ${rowUnselectedClasses} border border-dashed border-gray-400`}
                onClick={(event) => {
                    event.stopPropagation()
                    handleCreateCategory(parentCategoryId)
                }}
            >
                <span>{t('buttonCreateCategory')}</span>
                <AddIcon />
            </div>
        )
    }

    if (!categories.length && !allowCreate) {
        return <Callout type="warning">{t('calloutNoCategoriesToSelect')}</Callout>
    }

    return (
        <>
            <div className="overflow-x-scroll w-full no-swipe" ref={scrollRef}>
                <div className="flex flex-row gap-2 w-full">
                    <div className={colClasses}>
                        <div className={labelRowClasses}>{`${t(`labelCategoryLevel`)}: ${1}`}</div>
                        {getPossibleRootCategories.map((category) => {
                            return (
                                <div
                                    key={category.id}
                                    className={`${rowDefaultClasses} ${currentCategoryPath?.includes(category.id || '') ? category.isDeleted ? rowDeletedSelectedClasses : rowSelectedClasses : category.isDeleted ? rowDeletedUnselectedClasses : rowUnselectedClasses}`}
                                    onClick={() => !disableInputs && category.id && toggleTag(category.id, 0)}>
                                    <span className="">{category.name}</span>
                                    {category.isDeleted && <DeleteIcon />}
                                </div>
                            )
                        })}
                        {renderCreateButton('_')}
                    </div>
                    {currentCategoryPath && currentCategoryPath.map((tagId, key) => {
                        const childCategories = getPossibleChildCategories(tagId)
                        if (childCategories.length === 0 && !allowCreate) return null
                        return (
                            <div className={colClasses} key={tagId}>
                                <div className={labelRowClasses}>{`${t(`labelCategoryLevel`)}: ${key + 2}`}</div>
                                {childCategories.map((category) => {
                                    return (
                                        <div
                                            key={category.id}
                                            className={`${rowDefaultClasses} ${currentCategoryPath?.includes(category.id || '') ? category.isDeleted ? rowDeletedSelectedClasses : rowSelectedClasses : category.isDeleted ? rowDeletedUnselectedClasses : rowUnselectedClasses}`}
                                            onClick={() => !disableInputs && category.id && toggleTag(category.id, key + 1)}>
                                            <span className="">{category.name}</span>
                                            {category.isDeleted && <DeleteIcon />}
                                        </div>
                                    )
                                })}
                                {renderCreateButton(tagId)}
                            </div>
                        )
                    })}
                </div>
            </div>
            {allCategories.some(category => category.isDeleted) && (
                <div className="mt-2">
                    <Callout type="warning">
                        {t('calloutSomeCategoriesDeleted')}
                    </Callout>
                </div>
            )}
        </>
    )

}

export default memo(CategorySelector)

